import * as Yup from 'yup';
import * as _ from 'lodash';
import { pathnames } from 'lib/routes';
import { blankRepresentative } from 'lib/factories';

export const REQUIRED = 'Required';
export const REQUIRED_DETAILS = 'Please specify';
export const INVALID_EMAIL = 'Not a valid email address';
export const INVALID_VALUE = 'Please select a value';
export const INVALID_DATE = 'Invalid date';
export const INVALID_URL = 'Please enter a valid URL (e.g. https://example.com)';
export const TOO_LONG = 'Too long';
export const PERCENT_OWNERSHIP_MIN = 'Percentage of ownership must be at least 20%';
export const PERCENT_OWNERSHIP_MAX = 'Percentage of ownership cannot exceed 100%';
export const N_OWNERS_MIN = 'Invalid value';
export const N_OWNERS_MAX = 'There cannot be more than 5 beneficial owners';
export const AGREE_TO_TERMS = 'Please agree to the Terms and Conditions';

export const emailSchema = Yup.object({
  email: Yup
    .string()
    .required(REQUIRED)
    .email(INVALID_EMAIL),
})

export const accountTypeSchema = Yup.object({
  account_type: Yup
    .string()
    .required(REQUIRED)
    .oneOf(['individual', 'entity'], INVALID_VALUE),
})

export const addressSchema = Yup.object({
  address: Yup
    .string()
    .required(REQUIRED)
    .max(255, TOO_LONG),
  address_2: Yup
    .string()
    .max(255, TOO_LONG),
  city: Yup
    .string()
    .required(REQUIRED)
    .max(255, TOO_LONG),
  state: Yup
    .string()
    .required(REQUIRED),
  zip_postal_code: Yup
    .string()
    .required(REQUIRED)
    .max(255, TOO_LONG),
  country: Yup
    .string()
    .default('US')
    .required(REQUIRED)
})

export const individualInquirySchema = Yup.object({
  individual: Yup.object({
    persona_inquiry_id: Yup
      .string()
      .required(REQUIRED)
      .max(255, TOO_LONG),
  })
})

export const contactInfoSchema = Yup.object({
  individual: Yup.object({
    first_name: Yup
      .string()
      .required(REQUIRED)
      .max(255, TOO_LONG),
    middle_name: Yup
      .string()
      .max(255, TOO_LONG),
    last_name: Yup
      .string()
      .required(REQUIRED)
      .max(255, TOO_LONG),
    phone: Yup
      .string()
      .required(REQUIRED)
      .max(255, TOO_LONG),
    address: addressSchema,
  })
})

export const businessInfoSchema = Yup.object({
  entity: Yup.object({
    name: Yup
      .string()
      .required(REQUIRED)
      .max(255, TOO_LONG),
    company_url: Yup
      .string()
      .url(INVALID_URL)
      .max(255, TOO_LONG),
    kind: Yup
      .string()
      .required(REQUIRED)
      .oneOf(['corporation', 'llc', 'partnership', 'other'], INVALID_VALUE),
    address: addressSchema,
  })
})

export const authorizedRepresentativeSchema = Yup.object({
  first_name: Yup
    .string()
    .max(255, TOO_LONG),
  last_name: Yup
    .string()
    .max(255, TOO_LONG),
  email: Yup
    .string()
    .email(INVALID_EMAIL),
  phone: Yup
    .string()
    .max(255, TOO_LONG),
})

export const businessRepresentativesSchema = Yup.object({
  entity: Yup.object({
    authorized_representatives: Yup
      .array()
      .of(authorizedRepresentativeSchema)
      .ensure()
      // remove blank objects from array
      .compact((rep) => {
        return _.isEqual(rep, blankRepresentative)
      })
      // require at least one representative (non-empty array)
      .required(REQUIRED),
  })
})

// all the same fields as an individual + the percentage ownership
export const beneficialOwnerSchema = Yup.object({
  key: Yup
    .string()
    .required(REQUIRED),
  percentage_ownership: Yup
    .number()
    .required(REQUIRED)
    .min(20, PERCENT_OWNERSHIP_MIN)
    .max(100, PERCENT_OWNERSHIP_MAX),
  email: Yup
    .string()
    .required(REQUIRED)
    .email(INVALID_EMAIL),
})

export const businessOwnersSchema = Yup.object({
  entity: Yup.object({
    beneficial_owners: Yup
      .array()
      .of(beneficialOwnerSchema)
      .ensure()
      .min(0, N_OWNERS_MIN)
      .max(5, N_OWNERS_MAX),
  })
})

export const businessDocsSchema = Yup.object({
  entity: Yup.object({
    certificate_of_good_standing: Yup
      .mixed()
      .required(REQUIRED),
    organization_documents: Yup
      .mixed()
      .required(REQUIRED),
    aml_policy: Yup
      .mixed()
      .required(REQUIRED),
    legal_entity_identifier: Yup
      .string()
      .required(REQUIRED)
      .max(255, TOO_LONG),
    ein: Yup
      .string()
      .max(255, TOO_LONG),
  })
})

export const ecpStatusSchema = Yup.object({
  ecp_status: Yup
    .string()
    .oneOf([
      '', // does not qualify
      'eligible_individual',
      'hedging_individual',
      'hedging_entity',
      'large_entity',
      'swap_dealer',
      'security_based_swap_dealer',
      'major_swap_participant',
      'major_security_based_swap_participant',
      'financial_institution',
      'insurance_company',
      'investment_company',
      'floor_broker_trader',
      'commodity_pool',
      'employee_benefit_plan',
      'governmental_entity',
      'broker_dealer',
      'broker_dealer_large_entity',
      'associated_person',
      'investment_bank_holding_company',
      'futures_commission_merchant',
      'futures_commission_large_entity',
    ], INVALID_VALUE),
})

export const backgroundInfoSchema = Yup.object({
  registration_revoked: Yup
    .boolean()
    .nullable()
    .required(REQUIRED),
  convicted_felon: Yup
    .boolean()
    .nullable()
    .required(REQUIRED),
  misdemeanor: Yup
    .boolean()
    .nullable()
    .required(REQUIRED),
  under_investigation: Yup
    .boolean()
    .nullable()
    .required(REQUIRED),
  background_details: Yup
    .string()
    // details are required if any of the above are true
    .when(['registration_revoked', 'convicted_felon', 'misdemeanor', 'under_investigation'], {
      is: (registration_revoked, convicted_felon, misdemeanor, under_investigation) => {
        return registration_revoked || convicted_felon || misdemeanor || under_investigation;
      },
      then: Yup.string().required(REQUIRED_DETAILS),
    })
})

export const completeApplicationSchema = Yup.object({
  agree: Yup
    .boolean()
    .default(false)
    .required(REQUIRED)
    .oneOf([true], AGREE_TO_TERMS),
  signature: Yup
    .string()
    .required(REQUIRED),
  referral_source: Yup
    // selected options are cast as a comma-delimited string
    .string()
    .max(255, TOO_LONG),
  referral_source_other: Yup
    .string()
    .max(255, TOO_LONG),
})

export const beneficialOwnerCountrySchema = Yup.object({
  individual: Yup.object({
    address: Yup.object({
      country: Yup
        .string()
        .default('US')
        .required(REQUIRED)
    })
  })
})

const validation = {
  [pathnames.root]: emailSchema,
  [pathnames.account_type]: accountTypeSchema,
  [pathnames.contact_info]: individualInquirySchema,
  [pathnames.business_info]: businessInfoSchema,
  [pathnames.business_representatives]: businessRepresentativesSchema,
  [pathnames.business_owners]: businessOwnersSchema,
  [pathnames.business_docs]: businessDocsSchema,
  [pathnames.ecp_status]: ecpStatusSchema,
  [pathnames.background_info]: backgroundInfoSchema,
  [pathnames.complete_application]: completeApplicationSchema,
  [pathnames.beneficial_owner]: beneficialOwnerCountrySchema,
  [pathnames.beneficial_owner_contact_info]: contactInfoSchema,
  [pathnames.beneficial_owner_verification]: individualInquirySchema,
}

export default validation;
